/**
 * MidCycleProrationPreview
 * ---------------------------------------------------------------------------
 * Componente visual de PRÉVIA do ajuste pro-rata mid-cycle exibido dentro
 * do EditSubscriptionModal (Fase C do projeto Pro-Rata em Edição de
 * Recorrência).
 *
 * RELEASE:
 *   2026-06-27 v1.1.0 - Exibe bloco "Crédito disponível do cliente" lendo
 *                       `creditos_cliente` da RPC preview_mid_cycle_proration
 *                       v1.0.2: saldo atual, consumo previsto (quando o modo
 *                       é create_credit_note), crédito a gerar (downgrade) e
 *                       saldo após salvar.
 *   2026-06-17 v1.0.0 - Versão inicial (Fase C).
 *
 * Mostra:
 *  - Resumo (charge / credit / nada);
 *  - Fator pro-rata e dias restantes do ciclo atual;
 *  - Diferenças item a item entre o contrato atual e o que está sendo
 *    editado no modal.
 *
 * Não persiste nada — é apenas leitura. Toda lógica vive na RPC
 * `preview_mid_cycle_proration`.
 */
import { ArrowDownCircle, ArrowUpCircle, Info, Loader2, Wallet } from 'lucide-react';
import { InlineNotice } from '@/components/ui/inline-notice';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import type { MidCycleProrationPreview } from '@/hooks/useMidCycleProrationPreview';

interface Props {
  data: MidCycleProrationPreview | null | undefined;
  isLoading: boolean;
  /** v1.2.0 — controla checkbox "Usar crédito disponível" no modo 'ask'. */
  consumeCredit?: boolean;
  onConsumeCreditChange?: (v: boolean) => void;
}

function formatBRL(v: number): string {
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export function MidCycleProrationPreview({ data, isLoading, consumeCredit, onConsumeCreditChange }: Props) {
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Calculando prévia de ajuste mid-cycle...
      </div>
    );
  }
  if (!data) return null;

  // Bloco de crédito do cliente (v1.1.0) — exibido sempre que houver saldo
  // disponível, consumo previsto OU crédito a gerar. É renderizado como
  // SIBLING do InlineNotice principal para que apareça inclusive nos casos
  // "silenciosos" (sem ajuste mid-cycle aplicável).
  const credit = data.creditos_cliente ?? null;
  const hasCreditBlock =
    !!credit &&
    (credit.saldo_disponivel > 0 ||
      credit.consumo_previsto > 0 ||
      credit.credito_a_gerar > 0);

  const creditBlock = hasCreditBlock && credit ? (
    <InlineNotice variant="info" icon={Wallet} className="text-xs space-y-1">
      <div className="font-semibold">Crédito disponível do cliente</div>
      <div className="space-y-0.5">
        <div className="flex items-center justify-between gap-2">
          <span className="opacity-80">Saldo atual</span>
          <span className="font-semibold tabular-nums">{formatBRL(credit.saldo_disponivel)}</span>
        </div>
        {credit.consumo_previsto > 0 && (
          <div className="flex items-center justify-between gap-2">
            <span className="opacity-80">Será consumido ao salvar</span>
            <span className="font-semibold tabular-nums text-amber-700 dark:text-amber-400">
              -{formatBRL(credit.consumo_previsto)}
            </span>
          </div>
        )}
        {credit.credito_a_gerar > 0 && (
          <div className="flex items-center justify-between gap-2">
            <span className="opacity-80">Crédito a gerar (downgrade)</span>
            <span className="font-semibold tabular-nums text-emerald-700 dark:text-emerald-400">
              +{formatBRL(credit.credito_a_gerar)}
            </span>
          </div>
        )}
        <div className="flex items-center justify-between gap-2 border-t border-current/15 mt-1 pt-1">
          <span className="opacity-80">Saldo após salvar</span>
          <span className="font-semibold tabular-nums">{formatBRL(credit.saldo_apos_operacao)}</span>
        </div>
      </div>
      {/* v1.2.0 — Checkbox de confirmação quando a política for 'ask' */}
      {data.action === 'charge' &&
        credit.saldo_disponivel > 0 &&
        credit.politica_consumo === 'ask' &&
        onConsumeCreditChange && (
          <label className="flex items-start gap-2 pt-2 mt-1 border-t border-current/15 cursor-pointer">
            <Checkbox
              id="consume-credit-confirm"
              checked={!!consumeCredit}
              onCheckedChange={(v) => onConsumeCreditChange(v === true)}
              className="mt-0.5"
            />
            <span className="text-[11px] leading-tight">
              <strong>Aproveitar crédito disponível</strong> ({formatBRL(credit.saldo_disponivel)})
              ao salvar. O valor será abatido da cobrança pro-rata. Se o saldo cobrir tudo,
              nenhuma cobrança será criada.
            </span>
          </label>
        )}
      {/* Aviso quando a política está desligada mas há saldo */}
      {data.action === 'charge' &&
        credit.saldo_disponivel > 0 &&
        credit.consumo_previsto === 0 &&
        credit.politica_consumo !== 'ask' && (
          <div className="text-[11px] opacity-70 pt-1 border-t border-current/15">
            O saldo acima é informativo. O abatimento automático está desligado nas
            configurações financeiras do tenant. Ative em <strong>Configurações → Financeiro
            → Aproveitar crédito do cliente automaticamente</strong> para consumir esse saldo
            em ajustes pro-rata.
          </div>
        )}
      {credit.lista_disponivel.length > 0 && (
        <div className="pt-1 border-t border-current/15">
          <div className="text-[10px] uppercase tracking-wide opacity-70 mb-0.5">
            Créditos ({credit.lista_disponivel.length})
          </div>
          <div className="space-y-0.5">
            {credit.lista_disponivel.slice(0, 5).map((c) => (
              <div key={c.id} className="flex items-center justify-between gap-2 text-[11px]">
                <span className="truncate opacity-80">
                  {c.origem}
                  {c.expira_em ? ` · expira ${new Date(c.expira_em).toLocaleDateString('pt-BR')}` : ''}
                </span>
                <span className="tabular-nums">{formatBRL(c.saldo)}</span>
              </div>
            ))}
            {credit.lista_disponivel.length > 5 && (
              <div className="text-[10px] opacity-60">
                +{credit.lista_disponivel.length - 5} crédito(s) adicional(is)
              </div>
            )}
          </div>
        </div>
      )}
    </InlineNotice>
  ) : null;

  // Casos não aplicáveis com motivos "silenciosos" (não polui a UI)
  const silentReasons = new Set([
    'no_active_paid_cycle',
    'no_diff',
    'pro_rata_disabled',
    'mode_none',
    'apply_on_subscription_edit_disabled',
    'apply_on_plan_change_disabled',
  ]);

  if (!data.applicable && data.reason && silentReasons.has(data.reason)) {
    // Mantemos um aviso bem discreto apenas quando o desligamento é por config
    if (
      data.reason === 'pro_rata_disabled' ||
      data.reason === 'apply_on_subscription_edit_disabled' ||
      data.reason === 'mode_none'
    ) {
      return (
        <div className="space-y-2">
          <InlineNotice variant="info" className="text-xs">
            {data.summary}
          </InlineNotice>
          {creditBlock}
        </div>
      );
    }
    // Mesmo silencioso, ainda exibimos o bloco de crédito quando há saldo.
    return creditBlock ? <div className="space-y-2">{creditBlock}</div> : null;
  }

  const isCharge = data.action === 'charge';
  const isCredit = data.action === 'credit';
  const variant: 'warning' | 'success' | 'info' =
    isCharge ? 'warning' : isCredit ? 'success' : 'info';
  const Icon = isCharge ? ArrowUpCircle : isCredit ? ArrowDownCircle : Info;

  return (
    <div className="space-y-2">
      <InlineNotice variant={variant} icon={Icon} className="text-xs space-y-2">
        <div className="space-y-1">
          <div className="font-semibold">
            {isCharge && 'Ajuste pro-rata: cobrança extra'}
            {isCredit && 'Ajuste pro-rata: crédito em conta'}
            {!isCharge && !isCredit && 'Prévia de ajuste pro-rata'}
          </div>
          <div>{data.summary}</div>
          {data.ciclo && (
            <div className="flex flex-wrap items-center gap-1.5 pt-1">
              <Badge variant="outline" className="text-[10px]">
                Ciclo #{data.ciclo.ciclo_numero}
              </Badge>
              <Badge variant="outline" className="text-[10px]">
                {data.ciclo.dias_restantes}/{data.ciclo.dias_totais} dia(s) restantes
              </Badge>
              <Badge variant="outline" className="text-[10px]">
                Fator {data.ciclo.pro_rata_factor.toFixed(4)}
              </Badge>
              <Badge variant="outline" className="text-[10px]">
                Modo: {data.config.mode_effective === 'charge_immediately'
                  ? 'imediato'
                  : data.config.mode_effective === 'add_to_next_invoice'
                  ? 'próxima fatura'
                  : 'desligado'}
              </Badge>
            </div>
          )}
        </div>

        {data.diff.length > 0 && (
          <div className="border-t border-current/15 pt-2">
            <div className="text-[11px] uppercase tracking-wide opacity-70 mb-1">
              Diferenças por item
            </div>
            <div className="space-y-1">
              {data.diff.map((row) => (
                <div
                  key={row.product_id}
                  className="flex items-center justify-between gap-2 text-[11px]"
                >
                  <span className="truncate">
                    {row.descricao || row.product_id.slice(0, 8).toUpperCase()}
                  </span>
                  <span className="tabular-nums opacity-80">
                    {formatBRL(row.old_total)} → {formatBRL(row.new_total)}
                  </span>
                  <span
                    className={
                      'tabular-nums font-semibold ' +
                      (row.delta_pro_rata > 0
                        ? 'text-amber-700 dark:text-amber-400'
                        : row.delta_pro_rata < 0
                        ? 'text-emerald-700 dark:text-emerald-400'
                        : '')
                    }
                  >
                    {row.delta_pro_rata > 0 ? '+' : ''}
                    {formatBRL(row.delta_pro_rata)}
                  </span>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between border-t border-current/15 mt-2 pt-1 text-[11px]">
              <span className="opacity-70">Total pro-rata</span>
              <span className="font-semibold tabular-nums">
                {data.totals.delta_pro_rata > 0 ? '+' : ''}
                {formatBRL(data.totals.delta_pro_rata)}
              </span>
            </div>
          </div>
        )}
      </InlineNotice>
      {creditBlock}
    </div>
  );
}

export default MidCycleProrationPreview;
